import type { TaskColumnId } from '../types';

/**
 * Shared (board-level, not per-user) display config for the List view — visible columns, their
 * order, and their width. Persisted as the `config` JSON blob on the List-type `BoardViewDto`
 * (see `docs`/backend requirement "board view config"). Opaque to the backend, parsed here.
 */
export interface ListViewSharedConfig {
  /** Order of this array is the column order. */
  visibleColumns: TaskColumnId[];
  /** columnId -> width in px. A column absent here uses its registry default width. */
  columnWidths: Record<string, number>;
}

export const DEFAULT_VISIBLE_COLUMNS: TaskColumnId[] = [
  'title',
  'status',
  'priority',
  'assignee',
  'dueDate',
];

const EMPTY_CONFIG: ListViewSharedConfig = {
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  columnWidths: {},
};

/**
 * Parses the `config` JSON of a List-type `BoardViewDto`. Guards against a missing/corrupted
 * blob the same way the previous client-only store did: `visibleColumns` falls back to the
 * default unless it's a non-empty array of strings (built-in ids or custom-field UUIDs — this
 * module has no registry access to validate against, so any string is trusted).
 */
export function parseListViewConfig(configJson: string | null | undefined): ListViewSharedConfig {
  if (!configJson) return EMPTY_CONFIG;
  try {
    const parsed = JSON.parse(configJson) as Partial<ListViewSharedConfig>;
    const visibleColumns =
      Array.isArray(parsed.visibleColumns) &&
      parsed.visibleColumns.length > 0 &&
      parsed.visibleColumns.every((c) => typeof c === 'string')
        ? parsed.visibleColumns
        : DEFAULT_VISIBLE_COLUMNS;
    const columnWidths =
      parsed.columnWidths && typeof parsed.columnWidths === 'object'
        ? Object.fromEntries(
            Object.entries(parsed.columnWidths).filter(
              ([, w]) => typeof w === 'number' && w > 0 && Number.isFinite(w),
            ),
          )
        : {};
    return { visibleColumns, columnWidths };
  } catch {
    return EMPTY_CONFIG;
  }
}

export function serializeListViewConfig(config: ListViewSharedConfig): string {
  return JSON.stringify(config);
}
